import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import DataTable from "react-data-table-component";
import {
  useFetchEcommerceProductsMutation,
  useUpdateEcommerceProductMutation,
  useBulkAddEcommerceProductMutation,
} from "../../../features/tenant/ecommerceProduct/ecommerceProductApi";
import { useFetchEcommerceTagsMutation } from "../../../features/tenant/ecommerceTags/ecommerceTagsApi";
import { FiEdit, FiUpload } from "react-icons/fi";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import CSVReader from "../../../components/ImportCsv/CSVReader";
import { downloadCSV } from "../../../utils/downloadCSV";

const sampleProductsData = [
  {
    "Article No": "ART-001",
    "Product Name": "Nike Air Max Red",
    "Base MRP": "5999",
    "Retailer Price":"5999",
    "Distributor Price":"4999",
    "MOQ": "10",
    "Category Name": "Footwear",
    "Sub Category Name": "Sports Shoes",
    "Tags": "nike,sports,shoes",
    "Images": "https://example.com/images/nike-red-1.jpg,https://example.com/images/nike-red-2.jpg",
    "Color Name": "Red",
    "Color Hex": "#FF0000",
    "Size Name": "8",
    "Variant MRP": "5999",
    "Variant Inventory/Stock": "50",
    "Images JSON": `[{"urls":["https://genefied-saas-partner-staging.s3.ap-south-1.amazonaws.com/1789539955033-330380810black-bluetooth-headphone.webp"],"color_id":1,"color_name":"RED"},{"urls":["https://genefied-saas-partner-staging.s3.ap-south-1.amazonaws.com/1789539957685-103196018Mini-6W-BT-Speaker-blue.webp"],"color_id":3,"color_name":"WHITE"}]`
  },
  {
    "Article No": "ART-001",
    "Product Name": "Nike Air Max Red",
    "Base MRP": "5999",
    "Retailer Price":"5999",
    "Distributor Price":"4999",
    "MOQ": "10",
    "Category Name": "Footwear",
    "Sub Category Name": "Sports Shoes",
    "Tags": "nike,sports,shoes",
    "Images": "https://example.com/images/nike-red-1.jpg,https://example.com/images/nike-red-2.jpg",
    "Color Name": "Red",
    "Color Hex": "#FF0000",
    "Size Name": "9",
    "Variant MRP": "5999",
    "Variant Inventory/Stock": "40",
    "Images JSON": `[{"urls":["https://genefied-saas-partner-staging.s3.ap-south-1.amazonaws.com/1789539955033-330380810black-bluetooth-headphone.webp"],"color_id":1,"color_name":"RED"},{"urls":["https://genefied-saas-partner-staging.s3.ap-south-1.amazonaws.com/1789539957685-103196018Mini-6W-BT-Speaker-blue.webp"],"color_id":3,"color_name":"WHITE"}]`
  },
  {
    "Article No": "ART-001",
    "Product Name": "Nike Air Max Red",
    "Base MRP": "5999",
    "Retailer Price":"5999",
    "Distributor Price":"4999",
    "MOQ": "10",
    "Category Name": "Footwear",
    "Sub Category Name": "Sports Shoes",
    "Tags": "nike,sports,shoes",
    "Images": "https://example.com/images/nike-red-1.jpg,https://example.com/images/nike-red-2.jpg",
    "Color Name": "Red",
    "Color Hex": "#FF0000",
    "Size Name": "8, 9",
    "Variant MRP": "5999",
    "Variant Inventory/Stock": "10",
    "Images JSON": `[{"urls":["https://genefied-saas-partner-staging.s3.ap-south-1.amazonaws.com/1789539955033-330380810black-bluetooth-headphone.webp"],"color_id":1,"color_name":"RED"},{"urls":["https://genefied-saas-partner-staging.s3.ap-south-1.amazonaws.com/1789539957685-103196018Mini-6W-BT-Speaker-blue.webp"],"color_id":3,"color_name":"WHITE"}]`
  }
];

const EcommerceProductList = () => {
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [tags, setTags] = useState([]);
  
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [CSVData, setCSVData] = useState(null);
  const [fileName, setFileName] = useState(null);
  
  // Filters
  const initialRequest = {
    product_code: "",
    article_no: "",
    tags: "",
    status: "",
  };
  const [request, setRequest] = useState(initialRequest);
  
  // Pagination
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  
  const navigate = useNavigate();
  
  const vendorData = JSON.parse(sessionStorage.getItem("vendorData"));
  const tenantName = vendorData.tenant_name;
  const tenant_id = vendorData.tenant_id;
  const token = sessionStorage.getItem("access_token");

  // API Hooks
  const [
    fetchProducts,
    {
      data: productsData,
      isLoading: productsIsLoading,
      error: productsError,
    },
  ] = useFetchEcommerceProductsMutation();

  const [
    updateProduct,
    {
      data: updateProductData,
      error: updateProductError,
    },
  ] = useUpdateEcommerceProductMutation();

  const [
    bulkAddProduct,
    { isLoading: bulkAddIsLoading }
  ] = useBulkAddEcommerceProductMutation();

  const [
    fetchTags,
    {
      data: tagsData,
      error: tagsError,
    },
  ] = useFetchEcommerceTagsMutation();

  // Fetch tags on mount
  useEffect(() => {
    fetchTags({
      slug: tenant_id,
      token,
    });
  }, []);

  // Process tags data
  useEffect(() => {
    if (tagsData && tagsData.body) {
      setTags(tagsData.body.data || []);
    }
  }, [tagsData]);

  // Fetch products when filters or pagination changes
  useEffect(() => {
    loadProducts();
  }, [limit, offset]);

  const loadProducts = (req = request) => {
    fetchProducts({
      slug: tenant_id,
      token,
      limit: limit.toString(),
      offset: offset.toString(),
      ...req,
    });
  };

  // Handle products response
  useEffect(() => {
    if (productsData) {
      setProducts(productsData.body?.data || []);
      setTotalProducts(productsData.body?.count || 0);
    }
  }, [productsData]);

  // Handle products error
  useEffect(() => {
    if (productsError) {
      toast.error(productsError.data?.message || "Failed to fetch products");
    }
  }, [productsError]);

  // Handle update response
  useEffect(() => {
    if (updateProductData) {
      toast.success("Product updated successfully");
      loadProducts();
    }
  }, [updateProductData]);

  // Handle update error
  useEffect(() => {
    if (updateProductError) {
      toast.error(updateProductError.data?.message || "Failed to update product");
    }
  }, [updateProductError]);

  // Handle status toggle
  const handleStatusToggle = (row) => {
    const newStatus = row.status === "1" ? "2" : "1";
    updateProduct({
      slug: tenant_id,
      token,
      id: row.id.toString(),
      status: newStatus,
    });
  };

  // Handle search/filter
  const handleSearch = () => {
    setOffset(0); // Reset to first page
    loadProducts();
  };

  // Handle reset filters
  const handleReset = () => {
    setRequest(initialRequest);
    setOffset(0);
    loadProducts(initialRequest);
  };

  // Table columns
  const columns = [
    {
      name: "Article No",
      selector: (row) => row.article_no || "N/A",
      sortable: true,
      width: "150px",
    },
    {
      name: "Product Name",
      selector: (row) => row.product_name || "N/A",
      sortable: true,
      width: "200px",
    },
    {
      name: "MRP",
      selector: (row) => row.mrp ? `₹${row.mrp}` : "N/A",
      sortable: true,
      width: "100px",
    },
    {
      name: "Colors",
      cell: (row) => (
        <div className="text-xs">
          {row.color && row.color.length > 0 ? row.color.join(", ") : "N/A"}
        </div>
      ),
      width: "150px",
    },
    {
      name: "Sizes",
      cell: (row) => (
        <div className="text-xs">
          {row.size && row.size.length > 0 ? row.size.join(", ") : "N/A"}
        </div>
      ),
      width: "150px",
    },{
      name: "Tags",
      cell: (row) => (
        <div className="text-xs">
          {row.tag_names && row.tag_names.length > 0 ? row.tag_names.join(", ") : "N/A"}
        </div>
      ),
      width: "150px",
    },
    {
      name: "Status",
      cell: (row) => (
        <button
          onClick={() => handleStatusToggle(row)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 border-2 shadow-sm active:scale-95 ${
            row.status === "1"
              ? "bg-green-50 border-green-200 text-green-700 hover:bg-green-100 hover:border-green-300 pointer-events-auto cursor-pointer"
              : "bg-red-50 border-red-200 text-red-700 hover:bg-red-100 hover:border-red-300 pointer-events-auto cursor-pointer"
          }`}
        >
          {row.status === "1" ? "Active" : "Inactive"}
        </button>
      ),
      width: "120px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          onClick={() =>
            navigate(`/genefied/${tenantName}/ecommerce-products/edit/${row.id}`)
          }
          className="text-blue-600 hover:text-blue-800"
          title="Edit"
        >
          <FiEdit size={18} />
        </button>
      ),
      width: "100px",
    },
  ];

  // Pagination handlers
  const handlePageChange = (page) => {
    setOffset((page - 1) * limit);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setLimit(newPerPage);
    setOffset((page - 1) * newPerPage);
  };

  const handleBulkSubmit = async () => {
    if (!CSVData || !CSVData.parsed || CSVData.parsed.length === 0) {
      toast.error("Please upload a valid CSV file first");
      return;
    }

    const requiredColumns = ["Article No", "Product Name", "Base MRP", "Category Name"];
    const headers = CSVData.headers || [];
    const missingColumns = requiredColumns.filter(col => !headers.includes(col));

    if (missingColumns.length > 0) {
      toast.error(`Invalid CSV structure. Missing columns: ${missingColumns.join(", ")}`);
      return;
    }

    toast.loading("Uploading products...", { id: "bulk-upload" });

    try {
      const response = await bulkAddProduct({
        slug: tenant_id,
        token,
        rows: CSVData.parsed,
      }).unwrap();

      toast.dismiss("bulk-upload");
      if (response.success) {
        toast.success(response.message || "Bulk upload completed successfully");
        setShowBulkModal(false);
        setCSVData(null);
        setFileName(null);
        loadProducts();
      } else {
        toast.error(response.message || "Bulk upload failed");
      }
    } catch (err) {
      toast.dismiss("bulk-upload");
      toast.error(err?.data?.message || "Failed to complete bulk upload");
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Ecommerce Products</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowBulkModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors font-medium flex items-center gap-1 shadow-sm"
          >
            <FiUpload className="inline" /> Bulk Upload
          </button>
          <button
            onClick={() => navigate(`/genefied/${tenantName}/ecommerce-products/add`)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded transition-colors"
          >
            Add Product
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white shadow rounded-lg p-3 mb-4">
        <div className="border-b border-gray-300 bg-gray-50 rounded-t-lg p-3">
          <div className="flex space-x-2">
            <div className="w-52 h-10">
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                  Product Code
                </label>
                <input
                  type="search"
                  className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                  placeholder="Product Code..."
                  value={request.product_code}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRequest((prevState) => {
                      return { ...prevState, product_code: newValue };
                    });
                  }}
                />
              </div>
            </div>

            <div className="w-52 h-10">
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                  Article No
                </label>
                <input
                  type="search"
                  className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                  placeholder="Article No..."
                  value={request.article_no}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRequest((prevState) => {
                      return { ...prevState, article_no: newValue };
                    });
                  }}
                />
              </div>
            </div>

            <div className="w-52 h-10">
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                  Tags
                </label>
                <select
                  className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                  value={request.tags}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRequest((prevState) => {
                      return { ...prevState, tags: newValue };
                    });
                  }}
                >
                  <option value="">All Tags</option>
                  {tags && tags.length > 0 && tags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="w-52 h-10">
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                  Status
                </label>
                <select
                  className="w-full px-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500 inputp"
                  value={request.status}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRequest((prevState) => {
                      return { ...prevState, status: newValue };
                    });
                  }}
                >
                  <option value="">All Status</option>
                  <option value="1">Active</option>
                  <option value="2">Inactive</option>
                </select>
              </div>
            </div>

            <div className="flex flex-row gap-2 h-10">
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                  &nbsp;
                </label>
                <button
                  className="btn btn-outline-primary clear-both float-right p-2"
                  onClick={handleSearch}
                >
                  Search
                </button>
              </div>
              <div className="relative searchin">
                <label className="block text-sm font-medium capitalize leading-6 text-gray-900 mb-1">
                  &nbsp;
                </label>
                <button
                  className="btn btn-outline-primary clear-both float-right p-2"
                  onClick={handleReset}
                >
                  Clear Filter
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <DataTable
          columns={columns}
          data={products}
          progressPending={productsIsLoading}
          pagination
          paginationServer
          paginationTotalRows={totalProducts}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          highlightOnHover
          pointerOnHover
          responsive
          customStyles={{
            headRow: {
              style: {
                backgroundColor: "#f3f4f6",
                fontWeight: "600",
              },
            },
          }}
        />
      </div>

      {/* Bulk Upload Modal */}
      <ModalComponent
        show={showBulkModal}
        setShow={setShowBulkModal}
        size="lg"
      >
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">Bulk Upload Products</h2>
          
          <p className="text-sm text-gray-600 mb-6 text-center leading-relaxed">
            Upload your products in bulk using a CSV file. Duplicate Article Nos will be grouped into a single product with multiple variants (color & size combinations).
          </p>

          <div className="mb-6 flex justify-center">
            <button
              onClick={() => downloadCSV(sampleProductsData, "sample_ecommerce_products.csv")}
              className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 text-sm border border-blue-200 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              Download Sample CSV Template
            </button>
          </div>

          <div className="mb-6 border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
            <CSVReader
              setCSVData={setCSVData}
              setFileName={setFileName}
            />
            {fileName && (
              <div className="mt-3 text-center text-sm font-medium text-green-700 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200 inline-block mx-auto">
                Selected File: {fileName}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setShowBulkModal(false);
                setCSVData(null);
                setFileName(null);
              }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-gray-200"
              disabled={bulkAddIsLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleBulkSubmit}
              className={`bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-semibold transition-all shadow-sm active:scale-95 ${
                bulkAddIsLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={bulkAddIsLoading}
            >
              {bulkAddIsLoading ? "Uploading..." : "Submit File"}
            </button>
          </div>
        </div>
      </ModalComponent>
    </div>
  );
};

export default EcommerceProductList;
